import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  SportsSoccer as BallIcon,
  CalendarToday as CalendarTodayIcon,
  HandshakeOutlined as SponsorIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { COLORS } from './MAMCStyles';

const EmptyStateMessage = () => (
  <Box
    sx={{
      textAlign: 'center',
      py: { xs: 4, sm: 8, md: 12 },
      px: { xs: 2, sm: 4 },
      background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(20,20,20,0.95) 100%)',
      border: '1px solid rgba(218,165,32,0.3)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
      borderRadius: '20px',
      position: 'relative',
      overflow: 'hidden',
      maxWidth: '800px',
      mx: 'auto',
      mb: { xs: 4, sm: 6, md: 8 },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: COLORS.gradient.gold,
      },
      // Fixed background leak by extending the background and adjusting z-index
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -20, // Extend beyond the visible border
        left: 0,
        right: 0,
        height: 'calc(100% + 40px)', // Extend both top and bottom
        background: 'linear-gradient(135deg, rgba(0,0,0,0.9) 0%, rgba(20,20,20,0.95) 100%)',
        zIndex: -1,
        borderRadius: '20px', // Match the parent border radius
      },
    }}
  >
    {/* Event description - Added at the top */}
    <Box
      sx={{
        mb: 5,
        p: 3,
        borderRadius: 2,
        background: 'rgba(218,165,32,0.05)',
        border: '1px solid rgba(218,165,32,0.2)',
        maxWidth: '700px',
        mx: 'auto',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: COLORS.text.primary,
          fontSize: { xs: '0.85rem', sm: '0.95rem' },
          lineHeight: 1.8,
          textAlign: 'left',
          fontStyle: 'italic',
          mb: 2,
        }}
      >
        Dear Friends, Football Enthusiasts, and Community Members,
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: COLORS.text.primary,
          fontSize: { xs: '0.85rem', sm: '0.95rem' },
          lineHeight: 1.8,
          textAlign: 'left',
          mb: 2,
        }}
      >
        We're excited to invite you all to the Manoj Aryal Memorial Cup 2026.
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: COLORS.text.primary,
          fontSize: { xs: '0.85rem', sm: '0.95rem' },
          lineHeight: 1.8,
          textAlign: 'left',
          mb: 2,
        }}
      >
        After the huge success of the 2025 tournament, we're back again, bigger and better, to
        remember our dear friend and brother, Manoj Aryal, through the game he loved the most.
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: COLORS.text.primary,
          fontSize: { xs: '0.85rem', sm: '0.95rem' },
          lineHeight: 1.8,
          textAlign: 'left',
          mb: 2,
        }}
      >
        Just like last time, you can expect:
      </Typography>
      <Box sx={{ px: 2, mb: 2 }}>
        <Typography
          variant="body1"
          sx={{
            color: COLORS.text.primary,
            fontSize: { xs: '0.85rem', sm: '0.95rem' },
            lineHeight: 1.8,
            textAlign: 'left',
            mb: 1,
          }}
        >
          ⚽ Intense group stage matches followed by thrilling knockouts
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: COLORS.text.primary,
            fontSize: { xs: '0.85rem', sm: '0.95rem' },
            lineHeight: 1.8,
            textAlign: 'left',
            mb: 1,
          }}
        >
          🏆 Participation from some of the best Nepali football clubs in Finland
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: COLORS.text.primary,
            fontSize: { xs: '0.85rem', sm: '0.95rem' },
            lineHeight: 1.8,
            textAlign: 'left',
            mb: 1,
          }}
        >
          🍽️ Great food from food partners
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: COLORS.text.primary,
            fontSize: { xs: '0.85rem', sm: '0.95rem' },
            lineHeight: 1.8,
            textAlign: 'left',
          }}
        >
          💛 A full day of football, fun, and community vibes
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{
          color: COLORS.text.primary,
          fontSize: { xs: '0.85rem', sm: '0.95rem' },
          lineHeight: 1.8,
          textAlign: 'left',
          fontWeight: 'medium',
        }}
      >
        This event isn't just about football, it's about celebrating life, friendship, and the
        memories we shared with Manoj. We hope to see familiar faces and new ones in the crowd, all
        coming together to make this day special.
      </Typography>

      {/* Tournament Location and Date - Added here */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 3,
          mt: 3,
          p: 2,
          borderRadius: 1,
          bgcolor: 'rgba(218,165,32,0.1)',
          border: '1px solid rgba(218,165,32,0.2)',
        }}
      >
        <LocationIcon sx={{ color: COLORS.secondary, fontSize: '1.2rem' }} />
        <Typography
          variant="body1"
          sx={{
            color: COLORS.text.accent,
            fontSize: { xs: '0.85rem', sm: '1rem' },
            fontWeight: 'medium',
          }}
        >
          Laaksolahden urheilupuisto on August 1
        </Typography>
      </Box>
    </Box>

    <BallIcon
      sx={{
        fontSize: { xs: '4rem', sm: '6rem' },
        color: COLORS.secondary,
        opacity: 0.8,
        mb: 3,
        filter: 'drop-shadow(0 4px 12px rgba(218,165,32,0.5))',
        position: 'relative', // Ensure proper stacking context
        zIndex: 1, // Keep icon above background fixes
      }}
    />

    {/* Sponsorship Section - Moved to top for better visibility */}
    <Box
      sx={{
        mb: 5,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        borderBottom: `1px solid rgba(218,165,32,0.3)`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          color: COLORS.text.accent,
          fontSize: { xs: '1rem', sm: '1.2rem' },
        }}
      >
        <SponsorIcon sx={{ color: COLORS.secondary, fontSize: { xs: '1.2rem', sm: '1.4rem' } }} />
        <Typography fontWeight="bold">We're looking for sponsors!</Typography>
      </Box>

      <Typography
        variant="body1"
        sx={{
          color: COLORS.text.primary,
          maxWidth: '500px',
          textAlign: 'center',
          fontSize: { xs: '0.9rem', sm: '1rem' },
          mb: 1,
        }}
      >
        Support the MAMC Tournament and help us make it even better. Becoming a sponsor provides
        visibility for your business while supporting community sports.
      </Typography>

      <Button
        component={RouterLink}
        to="/contact"
        variant="contained"
        sx={{
          bgcolor: COLORS.secondary,
          color: '#000000',
          fontWeight: 'bold',
          '&:hover': {
            bgcolor: '#FFD700',
            color: '#000000',
          },
          px: 4,
          py: 1,
          fontSize: { xs: '0.85rem', sm: '0.95rem' },
          mt: 1,
        }}
      >
        Contact Us About Sponsorship
      </Button>
    </Box>

    <Typography
      variant="h4"
      sx={{
        color: COLORS.text.accent,
        fontWeight: 'bold',
        fontSize: { xs: '1.5rem', sm: '2.5rem' },
        mb: 2,
        textShadow: '0 2px 8px rgba(0,0,0,0.5)',
      }}
    >
      Stay Tuned!
    </Typography>
    <Typography
      variant="h6"
      sx={{
        color: COLORS.text.primary,
        fontSize: { xs: '1rem', sm: '1.25rem' },
        maxWidth: '600px',
        mx: 'auto',
        lineHeight: 1.6,
        mb: 4,
      }}
    >
      Groups and matches will be announced soon. Check back later for updates!
    </Typography>

    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        mt: 4,
        position: 'relative', // Ensure proper stacking context
        zIndex: 1, // Keep content above background fixes
        pb: 2, // Add padding at the bottom to ensure no content is cut off
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          color: COLORS.text.secondary,
          fontSize: { xs: '0.85rem', sm: '1rem' },
        }}
      >
        <CalendarTodayIcon sx={{ color: COLORS.secondary }} />
        <Typography>Tournament preparations are underway</Typography>
      </Box>
    </Box>
  </Box>
);

export default EmptyStateMessage;
